import React, { useEffect, useState } from "react";
import "../admin-affiliate-mobile.css";

import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase-config";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import SimpleQRCode from "./SimpleQRCode";

export default function AffiliateDashboard() {
  const [user, setUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [loading, setLoading] = useState(true);
  const [userDoc, setUserDoc] = useState(null);
  const [invited, setInvited] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setCheckingAuth(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!user) return;
    async function fetchUserData() {
      setLoading(true);
      setError("");
      try {
        // Saját user Firestore doksi
        const userSnap = await getDoc(doc(db, "users", user.uid));
        if (!userSnap.exists()) throw new Error("Nincs ilyen felhasználó a rendszerben.");
        const userData = userSnap.data();
        setUserDoc(userData);
        // Meghívottak lekérdezése (akik referredBy == saját referralCode)
        const invitedQ = query(collection(db, "users"), where("referredBy", "==", userData.referralCode));
        const invitedSnap = await getDocs(invitedQ);
        setInvited(invitedSnap.docs.map(d => d.data()));
      } catch (err) {
        setError("Nem sikerült betölteni a felhasználói adatokat.");
      }
      setLoading(false);
    }
    fetchUserData();
  }, [user]);

  if (checkingAuth) return <div className="flex justify-center items-center min-h-[50vh]">Betöltés...</div>;
  if (!user) return <div className="text-red-600 text-center mt-10">Nem vagy bejelentkezve! Kérlek jelentkezz be.</div>;
  if (loading) return <div className="flex justify-center items-center min-h-[50vh]"><div className="loader"></div></div>;
  if (error) return <div className="text-red-600 text-center mt-10">{error}</div>;
  if (!userDoc) return null;

  // Ha admin, irányítsuk át az admin dashboardra
  if (userDoc.role === "admin") {
    window.location.href = "/admin/dashboard";
    return null;
  }

  // Stat összesítés
  const totalClicks = userDoc.totalClicks ?? 0;
  const totalRegistrations = invited.length;
  const totalCommission = (userDoc.balance ?? 0);
  const emailVerified = user.emailVerified;

  // Kijelentkezés gomb handler
  function handleLogout() {
    import("firebase/auth").then(({ signOut }) => {
      signOut(auth).then(() => {
        localStorage.removeItem("affiliateToken");
        window.location.href = "/";
      });
    });
  }

  return (
    <div className="w-full max-w-2xl mx-auto bg-white/20 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/[0.08] p-6 md:p-10 mt-8 flex flex-col items-center gap-6 relative affiliate-dashboard-mobile">
      {/* Kijelentkezés gomb jobb felső sarokban */}
      <button
        onClick={handleLogout}
        className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white font-bold py-1.5 px-3 rounded-lg shadow text-xs z-20 transition-all duration-200 active:scale-95"
        style={{ minWidth: 0 }}
      >
        Kijelentkezés
      </button>
      {/* Early Bird Status Badge */}
      {userDoc.earlyBird && (
        <div className="bg-gradient-to-r from-[#e31937] to-[#b40000] rounded-xl p-4 mb-4 shadow-xl border border-red-400/30 w-full">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-2xl">🐦</span>
              <h3 className="text-lg font-bold text-white">
                Early Bird Felhasználó
              </h3>
              <span className="text-2xl">✨</span>
            </div>
            <p className="text-white/90 text-sm mb-2">
              Ingyenes hirdetéseid száma: <span className="font-bold text-yellow-200">{userDoc.freeListings || 5} / 5</span>
            </p>
            <div className="bg-white/20 rounded-lg p-2 backdrop-blur-sm">
              <p className="text-white text-xs font-medium">
                🎁 Korai regisztrációs előny aktív – 2025. szeptember 30-ig
              </p>
            </div>
          </div>
        </div>
      )}

      <h1 className="text-3xl md:text-4xl font-extrabold text-hopp-red drop-shadow mb-2 flex items-center gap-2">
        <svg className="w-8 h-8 text-hopp-red" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M13 16h-1v-4h-1m1-4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/></svg>
        Affiliate Dashboard
      </h1>
      <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="Kattintások" value={totalClicks} icon="👆" color="bg-blue-100" />
        <StatCard label="Meghívottak" value={totalRegistrations} icon="📝" color="bg-green-100" />
        <StatCard label="Jutalék összesen" value={totalCommission + ' Ft'} icon="💰" color="bg-yellow-100" />
      </div>
      <div className="w-full flex flex-col items-center gap-2 mt-4">
        <label className="font-bold text-gray-800">Ajánlói linked:</label>
        <div className="flex gap-2 items-center w-full justify-center">
          <input
            className="bg-white/80 text-black px-2 py-1 rounded w-full max-w-[320px] text-xs font-mono"
            value={window.location.origin + "/signup?ref=" + userDoc.referralCode}
            readOnly
            onClick={e => e.target.select()}
          />
          <button className="bg-hopp-red text-white px-3 py-1 rounded" onClick={() => navigator.clipboard.writeText(window.location.origin + "/signup?ref=" + userDoc.referralCode)}>Másolás</button>
        </div>
        <div className="my-2">
          <SimpleQRCode value={window.location.origin + "/signup?ref=" + userDoc.referralCode} size={96} />
        </div>
        <div className="mt-2 text-sm">
          <span className="font-bold">Email státusz:</span> {emailVerified ? <span className="text-green-600 font-bold">Megerősítve</span> : <span className="text-yellow-500 font-bold">Nincs megerősítve</span>}
        </div>
      </div>

      {/* Meghívottak részletes lista */}
      <div className="w-full mt-8">
        <h3 className="text-lg font-bold mb-2 text-hopp-red">Meghívottak</h3>
        {invited.length === 0 ? <div className="text-gray-400">Nincs meghívottad.</div> : (
          <table className="min-w-full text-xs bg-[#23272f] rounded-xl overflow-hidden">
            <thead>
              <tr className="bg-hopp-red text-white">
                <th className="px-2 py-1">Név</th>
                <th className="px-2 py-1">Email</th>
                <th className="px-2 py-1">Csatlakozás</th>
              </tr>
            </thead>
            <tbody>
              {invited.map(u => (
                <tr key={u.uid} className="border-b border-white/10">
                  <td className="px-2 py-1">{u.fullName}</td>
                  <td className="px-2 py-1">{u.email}</td>
                  <td className="px-2 py-1">{u.joinedAt?.toDate().toLocaleDateString() ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Jutalék történet */}
      <div className="w-full mt-8">
        <h3 className="text-lg font-bold mb-2 text-hopp-red">Jutalék történet</h3>
        {(!userDoc.commissionHistory || userDoc.commissionHistory.length === 0) ? <div className="text-gray-400">Nincs jutalékod.</div> : (
          <table className="min-w-full text-xs bg-[#23272f] rounded-xl overflow-hidden">
            <thead>
              <tr className="bg-hopp-red text-white">
                <th className="px-2 py-1">Forrás user</th>
                <th className="px-2 py-1">Dátum</th>
                <th className="px-2 py-1">Összeg (Ft)</th>
                <th className="px-2 py-1">Szint</th>
              </tr>
            </thead>
            <tbody>
              {userDoc.commissionHistory.map((c, i) => (
                <tr key={i} className="border-b border-white/10">
                  <td className="px-2 py-1">{c.sourceUserId}</td>
                  <td className="px-2 py-1">{c.date?.toDate().toLocaleString() ?? '-'}</td>
                  <td className="px-2 py-1">{c.amount}</td>
                  <td className="px-2 py-1">{c.level}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon, color }) {
  return (
    <div className={`rounded-xl p-4 shadow flex flex-col items-center ${color}`} style={{minHeight: 120}}>
      <span className="text-3xl mb-2">{icon}</span>
      <span className="text-xl font-bold text-gray-900">{value}</span>
      <span className="text-xs text-gray-600 mt-1 text-center">{label}</span>
    </div>
  );
}
